/**
 * AnalysisPanel — Spatial analysis + geometry processing tools
 * Tool list sets activeTool; SpatialAnalysisPanel hosts execute dialogs.
 */
import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
} from '@mui/material';
import { RadioButtonUnchecked, ContentCut, JoinInner, JoinFull, Search } from '@mui/icons-material';
import { tokens } from '../../../theme';
import { useGeoveraTheme } from '../../../theme/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setActiveTool, type SpatialTool } from '../../../store/slices/spatialAnalysisSlice';
import SpatialAnalysisPanel from '../../spatial/SpatialAnalysisPanel';
import GeometryProcessingPanel from '../../geometry-processing/GeometryProcessingPanel';

const TOOLS: {
  id: NonNullable<SpatialTool>;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'buffer',
    label: 'Buffer',
    description: 'Create proximity zones around features',
    icon: <RadioButtonUnchecked />,
  },
  {
    id: 'intersection',
    label: 'Intersection',
    description: 'Find overlapping areas between layers',
    icon: <JoinInner />,
  },
  {
    id: 'union',
    label: 'Union',
    description: 'Combine features from multiple layers',
    icon: <JoinFull />,
  },
  {
    id: 'clip',
    label: 'Clip',
    description: 'Extract features within a boundary',
    icon: <ContentCut />,
  },
  {
    id: 'spatial-query',
    label: 'Spatial Query',
    description: 'Select features by spatial relationship',
    icon: <Search />,
  },
];

const AnalysisPanel: React.FC = () => {
  const { palette } = useGeoveraTheme();
  const dispatch = useAppDispatch();
  const activeTool = useAppSelector((s) => s.spatialAnalysis.activeTool);
  const error = useAppSelector((s) => s.spatialAnalysis.error);
  const message = useAppSelector((s) => s.spatialAnalysis.message);
  const isProcessing = useAppSelector((s) => s.spatialAnalysis.isProcessing);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            color: palette.text.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            fontWeight: 600,
          }}
        >
          Spatial operations
        </Typography>
        <Typography sx={{ fontSize: tokens.fontSize.sm, color: palette.text.secondary, mt: 0.5 }}>
          Pick a tool, configure layers, then run. Results become new layers.
        </Typography>
      </Box>

      {(error || message) && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          {error && (
            <Alert severity="error" sx={{ fontSize: tokens.fontSize.sm }}>
              {error}
            </Alert>
          )}
          {message && !error && (
            <Alert severity="success" sx={{ fontSize: tokens.fontSize.sm }}>
              {message}
            </Alert>
          )}
        </Box>
      )}

      {isProcessing && (
        <Typography sx={{ px: 1.5, fontSize: tokens.fontSize.sm, color: palette.accent.primary }}>
          Processing…
        </Typography>
      )}

      <List dense disablePadding>
        {TOOLS.map((tool) => (
          <ListItemButton
            key={tool.id}
            selected={activeTool === tool.id}
            onClick={() => dispatch(setActiveTool(tool.id))}
            sx={{ px: 1.5, py: 0.75, mx: 0.5, borderRadius: tokens.radius.sm }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: palette.accent.primary }}>
              {React.cloneElement(tool.icon as React.ReactElement, { sx: { fontSize: 20 } })}
            </ListItemIcon>
            <ListItemText
              primary={tool.label}
              secondary={tool.description}
              primaryTypographyProps={{ sx: { fontSize: tokens.fontSize.md, fontWeight: 500 } }}
              secondaryTypographyProps={{ sx: { fontSize: tokens.fontSize.xs } }}
            />
          </ListItemButton>
        ))}
      </List>

      {/* Dialogs + execution handlers */}
      <SpatialAnalysisPanel />

      <Divider sx={{ my: 1 }} />

      <GeometryProcessingPanel />
    </Box>
  );
};

export default AnalysisPanel;
